// Singleton для координации refresh token логики между interceptor'ами.
// Гарантирует что только один refresh запрос выполняется одновременно.
// BroadcastChannel координирует вкладки одного origin'а — они разделяют один refresh-запрос.

class RefreshTokenManager {
  constructor() {
    this.isRefreshing = false;
    this.refreshQueue = [];
    this.refreshPromise = null;
    this._crossTabResolve = null;
    this._crossTabReject = null;
    this._channel = typeof BroadcastChannel !== 'undefined'
      ? new BroadcastChannel('auth_token_refresh')
      : null;
    this._initChannel();
  }

  _initChannel() {
    if (!this._channel) return;
    this._channel.onmessage = ({ data }) => {
      if (data.type === 'refresh_start' && !this.isRefreshing) {
        // Другая вкладка начала refresh — ставим свои запросы в очередь
        this.isRefreshing = true;
        this.refreshPromise = new Promise((resolve, reject) => {
          this._crossTabResolve = resolve;
          this._crossTabReject = reject;
        });
      }

      if (data.type === 'refresh_success') {
        if (data.token) localStorage.setItem('token', data.token);
        this._crossTabResolve?.(data.token);
        this._flushQueue(null, data.token);
        this._resetCrossTab();
      }

      if (data.type === 'refresh_failure') {
        const err = new Error('Refresh failed in another tab');
        err.isCrossTab = true;
        this._crossTabReject?.(err);
        this._flushQueue(err, null);
        this._resetCrossTab();
      }
    };
  }

  _flushQueue(error, token) {
    this.refreshQueue.forEach(({ resolve, reject }) =>
      error ? reject(error) : resolve(token)
    );
    this.refreshQueue = [];
  }

  _resetCrossTab() {
    this.isRefreshing = false;
    this.refreshPromise = null;
    this._crossTabResolve = null;
    this._crossTabReject = null;
  }

  isCurrentlyRefreshing() {
    return this.isRefreshing;
  }

  setRefreshing(value) {
    this.isRefreshing = value;
    if (!value) {
      this.refreshPromise = null;
    } else {
      this._channel?.postMessage({ type: 'refresh_start' });
    }
  }

  addToQueue(resolve, reject) {
    this.refreshQueue.push({ resolve, reject });
  }

  processQueue(error, token = null) {
    this._flushQueue(error, token);

    // Оповещаем другие вкладки того же origin'а (не ретранслируем cross-tab сообщения)
    if (!error && token) {
      this._channel?.postMessage({ type: 'refresh_success', token });
    } else if (error && !error.isCrossTab) {
      this._channel?.postMessage({ type: 'refresh_failure' });
    }
  }

  getQueueLength() {
    return this.refreshQueue.length;
  }

  setRefreshPromise(promise) {
    this.refreshPromise = promise;
  }

  getRefreshPromise() {
    return this.refreshPromise;
  }

  reset() {
    this.isRefreshing = false;
    this.refreshQueue = [];
    this.refreshPromise = null;
    this._crossTabResolve = null;
    this._crossTabReject = null;
  }
}

// Singleton instance
export default new RefreshTokenManager();
